import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface Props {
  page: number
  limit: number
  total: number
  totalPages: number
  onPageChange: (page: number) => void
  /** What is being counted, for the range label. */
  noun?: string
  /** Inside a table card, pass the card's side padding (`px-6 py-3`). */
  className?: string
}

/**
 * Page controls for a list.
 *
 * Controls that can never do anything are noise, so they hide at a single
 * page - but the count stays, because an absent control and a broken one look
 * identical otherwise. The range label is what makes the count legible — "1–25 of 137" tells
 * an operator there is more, which a pair of arrows alone does not.
 */
export function Pagination({
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  noun = 'items',
  className,
}: Props) {
  // Nothing at all is different from one page of results: hiding the count
  // entirely leaves no way to tell "everything fits" from "pagination is
  // broken", which is exactly how it read on a short list.
  if (total === 0) return null

  const single = totalPages <= 1
  const first = (page - 1) * limit + 1
  const last = Math.min(page * limit, total)

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4',
        className,
      )}
    >
      <p className="text-sm text-muted-foreground tabular-nums">
        {single
          ? `${total} ${total === 1 ? singular(noun) : noun}`
          : `${first}–${last} of ${total} ${noun}`}
      </p>
      {!single && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeft className="size-4" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground tabular-nums">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}
    </div>
  )
}

/** "1 inquiry", "1 consignment": the nouns passed in are all regular plurals. */
function singular(noun: string): string {
  if (noun.endsWith('ies')) return `${noun.slice(0, -3)}y`
  return noun.endsWith('s') ? noun.slice(0, -1) : noun
}
